import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import AddBoxIcon from '@mui/icons-material/AddBox';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { styled } from '@mui/material/styles';
import { formatNumber } from '../Common/Utilities';

const LENGTHS = ['15', '30', '60', '90', '120'];

// ---------------------------------------------------------------------------
// Styled wrappers — mirrors InvoiceDocumentEditorGrid styling
// ---------------------------------------------------------------------------
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
	'& .MuiTableCell-root': {
		fontSize: '0.75rem',
		color: 'inherit',
		border: 0,
		borderBottom: '1px solid rgba(224, 224, 224, 1)',
		cursor: 'pointer',
		margin: 0,
		outline: 0,
		padding: '0 10px 0 10px',
		borderRadius: 0,
		textDecoration: 'none',
		height: '52px'
	},
	'& .MuiTableRow-hover:hover': {
		backgroundColor: 'white !important'
	},
	'& .MuiTableCell-root .MuiInput-input, & .MuiTableCell-root select': {
		boxSizing: 'border-box',
		color: '#414141',
		height: '34px',
		padding: '7px 10px 7px 10px',
		width: '100%',
		outline: 'none',
		border: '1px solid #c6c6c6',
		borderRadius: 6,
		transition: '0.3s ease-in-out all',
		fontSize: '0.75rem'
	},
	'& .MuiTableCell-root .MuiInput-input-error': {
		border: '1px solid red !important'
	},
	'&::-webkit-scrollbar': { height: '0.5em', width: '0.2em' },
	'&::-webkit-scrollbar-thumb:vertical, &::-webkit-scrollbar-thumb:horizontal': {
		background: '#B2b2b2', borderRadius: 2, opacity: '0.5'
	}
}));

const StyledTablePagination = styled(TablePagination)(() => ({
	'& p': { margin: 0 }
}));

const toText = (value) => value === null || value === undefined ? '' : String(value);
const formatTime = (value) => {
	const digits = toText(value).replace(/\D/g, '').slice(0, 4);
	if (!digits) return '';
	// Pad with leading zeros so the stored value is always exactly 4 digits (HHMM)
	return digits.padStart(4, '0');
};
const normalizeLength = (value) => {
	const text = toText(value).trim();
	return text || '30';
};
const normalizeRate = (value) => {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed.toFixed(2) : '0.00';
};
const buildDefaultSpotLine = (invoiceDocument, tempId) => ({
	sequenceId: tempId,
	rowInvNumber: invoiceDocument?.invoiceNumber || '',
	runCode: 'Y',
	runDate: '',
	timeOfDay: '',
	rate: '0.00',
	length: '30',
	programDescription: ''
});
const sanitizeSpotLine = (line, index, invoiceDocument) => ({
	sequenceId: line?.sequenceId != null ? Number(line.sequenceId) : 0,
	uiOrder: index + 1,
	rowInvNumber: line?.rowInvNumber || invoiceDocument?.invoiceNumber || '',
	runCode: toText(line?.runCode).toUpperCase() === 'N' ? 'N' : 'Y',
	runDate: toText(line?.runDate).replace(/\D/g, '').slice(0, 6),
	timeOfDay: formatTime(toText(line?.timeOfDay).replace(/\D/g, '').slice(0, 4)),
	rate: normalizeRate(line?.rate),
	length: normalizeLength(line?.length),
	programDescription: toText(line?.programDescription).trim()
});

// ---------------------------------------------------------------------------
// TimeOfDayCellInput — handles auto zero-padding on blur (e.g. "930" → "0930")
// ---------------------------------------------------------------------------
const TimeOfDayCellInput = ({ value, disabled, errorClass, onCommit }) => {
	const [raw, setRaw] = React.useState(toText(value));

	React.useEffect(() => {
		if (document.activeElement?.dataset?.todInput !== 'true') {
			setRaw(toText(value));
		}
	}, [value]);

	const handleChange = (e) => {
		const digits = e.target.value.replace(/\D/g, '').slice(0, 4);
		setRaw(digits);
	};

	const handleBlur = () => {
		const padded = formatTime(raw) || '0000'; // always at least 0000, never empty
		setRaw(padded);
		onCommit(padded);
	};

	return (
		<input
			data-tod-input="true"
			type="text"
			className={`MuiInputBase-input MuiInput-input${errorClass}`}
			style={{ textAlign: 'right', width: '100%' }}
			value={raw}
			placeholder="HHMM"
			maxLength={4}
			disabled={disabled}
			onChange={handleChange}
			onBlur={handleBlur}
			onKeyDown={(e) => {
				if (e.key === 'Enter') e.target.blur();
			}}
		/>
	);
};

// ---------------------------------------------------------------------------
// Per-row validation - mirrors the server-side GetValidationMessageForRow logic.
// Returns a map { fieldName: errorMessage } for every failing field.
// An empty map means the row is valid.
// ---------------------------------------------------------------------------
const validateRow = (line, invDoc) => {
	const errors = {};

	// runCode: required, single char, must be Y or N
	const rc = toText(line.runCode).toUpperCase();
	if (rc !== 'Y' && rc !== 'N') {
		errors.runCode = "Run code must be 'Y' or 'N'";
	}

	// runDate: required, exactly 6 digits, valid date, within service term
	const rd = toText(line.runDate).replace(/\D/g, '');
	if (!rd || rd.length !== 6) {
		errors.runDate = 'Run date must be 6 digits (YYMMDD)';
	} else {
		const year = 2000 + parseInt(rd.slice(0, 2), 10);
		const month = parseInt(rd.slice(2, 4), 10) - 1;
		const day = parseInt(rd.slice(4, 6), 10);
		const runDateObj = new Date(year, month, day);
		if (isNaN(runDateObj.getTime()) || runDateObj.getMonth() !== month) {
			errors.runDate = 'Run date is not a valid date (YYMMDD)';
		} else {
			const startStr = invDoc?.serviceTermStartDateString;
			const endStr = invDoc?.serviceTermEndDateString;
			if (startStr && endStr) {
				const start = new Date(startStr);
				const end = new Date(endStr);
				if (runDateObj < start || runDateObj > end) {
					errors.runDate = `Run date must be between ${startStr} and ${endStr}`;
				}
			}
		}
	}

	// timeOfDay: frontend always sends exactly 4 digits via TimeOfDayCellInput (auto-pads to 0000 minimum)
	// validation commented out — kept for reference, re-enable if strict server-side check is needed
	// const tod = toText(line.timeOfDay).replace(/\D/g, '');
	// if (tod.length > 0) {
	// 	const padded = tod.padStart(4, '0');
	// 	const hh = parseInt(padded.slice(0, 2), 10);
	// 	const mm = parseInt(padded.slice(2, 4), 10);
	// 	if (hh > 23 || mm > 59) {
	// 		errors.timeOfDay = 'Time of day is invalid (HHMM, 00:00-23:59)';
	// 	}
	// }

	// rate: required, parseable as a decimal number
	const rateVal = parseFloat(line.rate);
	if (isNaN(rateVal)) {
		errors.rate = 'Rate must be a valid number';
	}

	// length: required, at most 3 characters
	const lenVal = toText(line.length).trim();
	if (!lenVal) {
		errors.length = 'Spot length is required';
	} else if (lenVal.length > 3) {
		errors.length = 'Spot length must be 3 characters or less';
	}

	return errors;
};

const getInvoiceAmount = (invoiceDocument) => {
	const details = invoiceDocument?.invoiceDocumentEditorDetails || [];
	return details.reduce((sum, line) => sum + Number(line?.ioInvoiceAmount || 0), 0);
};

const roundCurrency = (value) => {
	const amount = Number(value || 0);
	return Number.isFinite(amount) ? Number(amount.toFixed(2)) : 0;
};

const getNextTempId = (lines) => {
	// New (unsaved) lines get a unique negative temp ID so they are distinguishable
	// on the client. The BL clamps any sequenceId <= 0 to 0 before calling the SP,
	// which tells the DB to treat the row as a new insert and assign the real ID.
	const ids = (lines || []).map(l => Number(l?.sequenceId || 0));
	const minId = ids.length > 0 ? Math.min(...ids) : 0;
	return minId <= 0 ? minId - 1 : -1;
};

const getGridValidationState = (lines, invoiceDocument) => {
	const rowErrors = {};
	lines.forEach(line => {
		const errs = validateRow(line, invoiceDocument);
		if (Object.keys(errs).length > 0) {
			rowErrors[line.sequenceId] = errs;
		}
	});

	const invalidRowsList = new Set(
		Object.entries(rowErrors)
			.filter(([, e]) => Object.keys(e || {}).length > 0)
			.map(([id]) => Number(id))
	);

	const invalidRowsMessage = {};

	// Collect unique error messages across all invalid rows (deduplicated)
	// so the parent toast shows each distinct error type once, not once per row.
	const uniqueRowErrors = new Set();
	Object.entries(rowErrors).forEach(([, errors]) => {
		if (Object.keys(errors || {}).length > 0) {
			Object.values(errors).forEach(msg => uniqueRowErrors.add(msg));
		}
	});
	if (uniqueRowErrors.size > 0) {
		invalidRowsMessage.__row_errors = {
			type: '1',
			errorMsg: Array.from(uniqueRowErrors).join('; ')
		};
	}

	const totalSpot = Number(invoiceDocument?.totalSpot || 0);
	if (lines.length !== totalSpot) {
		invalidRowsList.add(-1);
		invalidRowsMessage.__grid_totalSpot = {
			type: '1',
			errorMsg: 'Line count does not match with total spots value'
		};
	}

	const invoiceAmount = getInvoiceAmount(invoiceDocument);
	const rowsAmount = lines.reduce((sum, line) => sum + Number(line?.rate || 0), 0);
	if (roundCurrency(rowsAmount) !== roundCurrency(invoiceAmount)) {
		invalidRowsList.add(-2);
		invalidRowsMessage.__grid_invoiceAmount = {
			type: '1',
			errorMsg: 'Amount in lines does not match with invoice amount'
		};
	}

	if (invalidRowsList.size > 0 && Object.keys(invalidRowsMessage).length === 0) {
		invalidRowsMessage.__grid_invalid = {
			type: '1',
			errorMsg: 'Broadcast spot lines are invalid'
		};
	}

	return {
		rowErrors,
		isGridDataValid: invalidRowsList.size === 0,
		invalidRowsList,
		invalidRowsMessage
	};
};

// ---------------------------------------------------------------------------
// RateCellInput — mirrors CurrencyEditInputCell from InvoiceDocumentEditorGrid
// Handles paste, raw-vs-formatted display, cursor preservation, and key guard
// ---------------------------------------------------------------------------
const isCompleteNumber = (v) => /^-?(?:\d+|\d*\.\d+)$/.test(v);

const RateCellInput = ({ value, disabled, errorClass, onCommit }) => {
	const inputRef = React.useRef(null);
	const cursorPosRef = React.useRef({ start: null, end: null });

	const [mode, setMode] = React.useState('text');
	const [rawValue, setRawValue] = React.useState(() =>
		parseFloat((value || '0').toString().replace(/,/g, '') || '0').toFixed(2)
	);
	const [formattedValue, setFormattedValue] = React.useState(() =>
		formatNumber(parseFloat((value || '0').toString().replace(/,/g, '') || '0'))
	);

	React.useEffect(() => {
		if (mode === 'number') return;
		const raw = parseFloat((value || '0').toString().replace(/,/g, '') || '0');
		setRawValue(raw.toFixed(2));
		setFormattedValue(formatNumber(raw) || '0.00');
	}, [value]);

	React.useLayoutEffect(() => {
		const { start, end } = cursorPosRef.current;
		if (inputRef.current && start !== null && end !== null) {
			inputRef.current.setSelectionRange(start, end);
		}
	});

	const handleFocus = () => {
		setMode('number');
		setRawValue(parseFloat(rawValue).toFixed(2));
	};

	const handlePaste = (e) => {
		if (disabled) return;
		e.preventDefault();
		const pasteText = e.clipboardData.getData('text') || '';
		const cleaned = pasteText.replace(/[^0-9.\-]/g, '');
		if (!cleaned) return;
		setMode('number');
		setRawValue(cleaned);
		if (isCompleteNumber(cleaned)) {
			const num = Number(cleaned);
			const fixed = num.toFixed(2);
			setFormattedValue(formatNumber(fixed));
			onCommit(fixed);
		}
		requestAnimationFrame(() => {
			if (inputRef.current) {
				const len = cleaned.length;
				inputRef.current.setSelectionRange(len, len);
				cursorPosRef.current = { start: len, end: len };
			}
		});
	};

	const handleChange = (e) => {
		cursorPosRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd };
		const val = e.target.value;
		if (val === '') { setRawValue(''); onCommit('0'); return; }
		if (val === '-' || val === '.' || val === '-.') { setRawValue(val); return; }
		const cleaned = val.replace(/[^0-9.\-]/g, '');
		if (cleaned === '') { setRawValue(''); return; }
		if (cleaned === '-' || cleaned === '.' || cleaned === '-.') { setRawValue(cleaned); return; }
		if (!/^-?\d*\.?\d*$/.test(cleaned)) return;
		setRawValue(cleaned);
		if (isCompleteNumber(cleaned)) {
			const num = Number(cleaned);
			const fixed = num.toFixed(2);
			setFormattedValue(formatNumber(fixed));
			onCommit(fixed);
		}
	};

	const handleBlur = () => {
		let val = rawValue;
		if (val === '' || val === '-' || val === '.' || val === '-.') val = '0';
		const num = Number(val);
		const fixed = isNaN(num) ? '0.00' : num.toFixed(2);
		setRawValue(fixed);
		setFormattedValue(formatNumber(Number(fixed)) || '0.00');
		setMode('text');
		onCommit(fixed);
	};

	return (
		<input
			ref={inputRef}
			type="text"
			className={`MuiInputBase-input MuiInput-input${errorClass}`}
			style={{ textAlign: 'right', width: '100%' }}
			value={mode === 'number' && !disabled ? rawValue : formattedValue}
			maxLength={15}
			placeholder="Rate"
			disabled={disabled}
			onFocus={handleFocus}
			onBlur={handleBlur}
			onChange={handleChange}
			onPaste={handlePaste}
			onKeyDown={(e) => {
				if (e.ctrlKey || e.metaKey) return;
				const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'];
				if (!/[0-9.\-]/.test(e.key) && !allowed.includes(e.key)) e.preventDefault();
			}}
		/>
	);
};

export default function BroadcastSpotLines(props) {
	const {
		invoiceDocument,
		setParentState,
		editable,
		stateRef
	} = props;

	const [selectedIds, setSelectedIds] = useState([]);
	const [deletedSequenceIds, setDeletedSequenceIds] = useState([]);
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(10);
	// focusedCell: { sequenceId, field } — tracks which cell is in raw-edit mode
	const [focusedCell, setFocusedCell] = useState(null);
	// rawValues: { [sequenceId_field]: string } — raw text while the user is typing
	const [rawValues, setRawValues] = useState({});
	const [searchText, setSearchText] = useState('');

	const validationStateRef = useRef({
		rowErrors: {},
		isGridDataValid: false,
		invalidRowsList: new Set(),
		invalidRowsMessage: {}
	});

// rowErrors: { [sequenceId]: { runCode?, runDate?, timeOfDay?, rate?, length? } }
// An absent key or an empty object means the row is valid.
	const [rowErrors, setRowErrors] = useState({});

	const commitGridValidationState = (nextLines, nextInvoiceDocument = invoiceDocument) => {
		const nextState = getGridValidationState(nextLines, nextInvoiceDocument);
		validationStateRef.current = nextState;
		setRowErrors(nextState.rowErrors);
		return nextState;
	};

	const lines = useMemo(() => {
		const source = invoiceDocument?.broadcastSpotLines || [];
		return source.map((line, index) => sanitizeSpotLine(line, index, invoiceDocument));
	}, [invoiceDocument?.broadcastSpotLines, invoiceDocument?.invoiceNumber]);

	// Keep parent payload normalized for untouched rows too (e.g. 752 -> 0752)
	useEffect(() => {
		const source = invoiceDocument?.broadcastSpotLines || [];
		if (source.length === 0) return;

		let changed = false;
		const normalized = source.map((line) => {
			const nextTimeOfDay = formatTime(toText(line?.timeOfDay).replace(/\D/g, '').slice(0, 4));
			if (toText(line?.timeOfDay) !== nextTimeOfDay) {
				changed = true;
			}
			return { ...line, timeOfDay: nextTimeOfDay };
		});

		if (!changed) return;

		setParentState({
			invoiceDocument: {
				...invoiceDocument,
				broadcastSpotLines: normalized
			},
			isDirty: false
		});
	}, [invoiceDocument?.broadcastSpotLines, invoiceDocument, setParentState]);

	// ---------------------------------------------------------------------------
	// Imperative ref - exposes validation state to InvoiceDocumentEditor
	// ---------------------------------------------------------------------------
	useImperativeHandle(stateRef, () => ({
		get isGridDataValid() {
			return validationStateRef.current.isGridDataValid;
		},
		get invalidRowsList() {
			return validationStateRef.current.invalidRowsList;
		},
		get invalidRowsMessage() {
			return validationStateRef.current.invalidRowsMessage;
		},
		get deletedSequenceIds() {
			return deletedSequenceIds;
		},
		get lines() {
			return lines;
		},
		revalidateAll(nextInvoiceDocument = invoiceDocument) {
			const sourceDoc = nextInvoiceDocument || invoiceDocument;
			const sourceLines = (sourceDoc?.broadcastSpotLines || []).map((line, index) =>
				sanitizeSpotLine(line, index, sourceDoc)
			);
			return commitGridValidationState(sourceLines, sourceDoc);
		},
		resetGridData(resetInvoiceDocument) {
			const resetLines = (resetInvoiceDocument?.broadcastSpotLines || []).map(
				(line, index) => sanitizeSpotLine(line, index, resetInvoiceDocument)
			);
			setRawValues({});
			setFocusedCell(null);
			setSelectedIds([]);
			setDeletedSequenceIds([]);
			setPage(0);
			commitGridValidationState(resetLines, resetInvoiceDocument);
		}
	}), [lines, invoiceDocument, deletedSequenceIds]);

	const updateLines = (nextLines, options = {}) => {
		const totalSpot = options.totalSpot ?? nextLines.length;
		const sanitized = nextLines.map((line, index) => sanitizeSpotLine({
			...line,
			rowInvNumber: invoiceDocument?.invoiceNumber || line?.rowInvNumber || ''
		}, index, invoiceDocument));

		setParentState({
			invoiceDocument: {
				...invoiceDocument,
				totalSpot,
				broadcastSpotLines: sanitized
			},
			isDirty: true
		});
	};

	// Auto-create blank lines from totalSpot when no real lines exist
	useEffect(() => {
		if (!invoiceDocument || lines.length > 0) return;
		const totalSpots = Number(invoiceDocument?.totalSpot || 0);
		if (!Number.isFinite(totalSpots) || totalSpots <= 0) return;
		const defaultLines = Array.from({ length: totalSpots }, (_, index) =>
			buildDefaultSpotLine(invoiceDocument, -(index + 1))
		);
		updateLines(defaultLines, { totalSpot: totalSpots });
	}, [invoiceDocument, lines.length]);

	// Re-validate all rows whenever service term changes
	useEffect(() => {
		if (lines.length === 0) return;
		commitGridValidationState(lines, invoiceDocument);
	}, [invoiceDocument?.serviceTermStartDateString, invoiceDocument?.serviceTermEndDateString]);

	// Re-validate whenever header values that affect grid-level checks are updated
	// (Net Amount -> invoiceDocumentEditorDetails[0].ioInvoiceAmount, Total Spots, etc.).
	useEffect(() => {
		if (lines.length === 0) return;
		commitGridValidationState(lines, invoiceDocument);
	}, [invoiceDocument?.invoiceDocumentEditorDetails, invoiceDocument?.totalSpot]);

	// Derived toolbar stats
	const sumRate = useMemo(
		() => lines.reduce((sum, line) => sum + (line.runCode === 'Y' ? Number(line.rate || 0) : 0), 0),
		[lines]
	);
	const spotCount = useMemo(() => lines.filter(l => l.runCode === 'Y').length, [lines]);

	// Permissions
	const canEdit = !!editable;
	const canAdd = canEdit && selectedIds.length === 0;
	const canDelete = canEdit && selectedIds.length > 0;
	const canCopy = canEdit && selectedIds.length > 0;

	// ---------------------------------------------------------------------------
	// Move handler (kept identical)
	// ---------------------------------------------------------------------------
	const handleMoveLine = (sequenceId, direction) => {
		const currentIndex = lines.findIndex(line => line.sequenceId === sequenceId);
		if (currentIndex < 0) return;

		const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
		if (targetIndex < 0 || targetIndex >= lines.length) return;

		const nextLines = [...lines];
		[nextLines[currentIndex], nextLines[targetIndex]] = [nextLines[targetIndex], nextLines[currentIndex]];

		commitGridValidationState(nextLines, invoiceDocument);
		updateLines(nextLines, { totalSpot: invoiceDocument?.totalSpot ?? nextLines.length });
	};

	// ---------------------------------------------------------------------------
	// Add / Delete (kept identical)
	// ---------------------------------------------------------------------------
	const handleAddLine = () => {
		const nextLines = [...lines, buildDefaultSpotLine(invoiceDocument, getNextTempId(lines))];
		commitGridValidationState(nextLines, {
			...invoiceDocument,
			totalSpot: nextLines.length,
			broadcastSpotLines: nextLines
		});
		updateLines(nextLines, { totalSpot: nextLines.length });
	};

	const handleDeleteSelected = () => {
		if (selectedIds.length === 0) return;
		const idSet = new Set(selectedIds);
		// Only track real DB sequence IDs (>0) — negative temp IDs were never saved
		const realDeleted = selectedIds.filter(id => id > 0);
		if (realDeleted.length > 0) {
			setDeletedSequenceIds(prev => [...new Set([...prev, ...realDeleted])]);
		}
		const nextLines = lines.filter(line => !idSet.has(line.sequenceId));
		commitGridValidationState(nextLines, {
			...invoiceDocument,
			totalSpot: nextLines.length,
			broadcastSpotLines: nextLines
		});
		setSelectedIds([]);
		updateLines(nextLines, { totalSpot: nextLines.length });
	};

	const handleCopyLine = () => {
		if (selectedIds.length === 0) return;
		const idSet = new Set(selectedIds);
		// Collect selected lines in their current source order, then append all
		// duplicates at the end so existing rows keep their uiOrder unchanged.
		let workingLines = [...lines];
		const selectedInOrder = lines.filter(l => idSet.has(l.sequenceId));
		selectedInOrder.forEach(source => {
			const duplicate = { ...source, sequenceId: getNextTempId(workingLines) };
			workingLines = [...workingLines, duplicate];
		});
		commitGridValidationState(workingLines, {
			...invoiceDocument,
			totalSpot: workingLines.length,
			broadcastSpotLines: workingLines
		});
		setSelectedIds([]);
		updateLines(workingLines, { totalSpot: workingLines.length });
	};

	// ---------------------------------------------------------------------------
	// Inline-edit helpers — update a single field on a single row
	// ---------------------------------------------------------------------------
	const rawKey = (seqId, field) => `${seqId}_${field}`;

	const handleFieldChange = (seqId, field, rawValue) => {
		setRawValues(prev => ({ ...prev, [rawKey(seqId, field)]: rawValue }));
	};

	const handleFieldBlur = (seqId, field) => {
		const raw = rawValues[rawKey(seqId, field)];
		if (raw === undefined) return;

		let finalValue = raw;
		if (field === 'runDate') {
			finalValue = toText(raw).replace(/\D/g, '').slice(0, 6);
		} else if (field === 'timeOfDay') {
			finalValue = formatTime(toText(raw).replace(/\D/g, '').slice(0, 4));
		} else if (field === 'rate') {
			finalValue = normalizeRate(raw);
		}

		const nextLines = lines.map(l =>
			l.sequenceId === seqId ? sanitizeSpotLine({ ...l, [field]: finalValue }, 0, invoiceDocument) : l
		);
		commitGridValidationState(nextLines, invoiceDocument);
		updateLines(nextLines);

		// Clear raw value so display reverts to formatted
		setRawValues(prev => {
			const next = { ...prev };
			delete next[rawKey(seqId, field)];
			return next;
		});
		setFocusedCell(null);
	};

	const handleSelectChange = (seqId, field, value) => {
		const nextLines = lines.map(l =>
			l.sequenceId === seqId ? sanitizeSpotLine({ ...l, [field]: value }, 0, invoiceDocument) : l
		);
		commitGridValidationState(nextLines, invoiceDocument);
		updateLines(nextLines);
	};

	// ---------------------------------------------------------------------------
	// Checkbox helpers
	// ---------------------------------------------------------------------------
	const isSelected = (seqId) => selectedIds.includes(seqId);

	const toggleRow = (seqId) => {
		setSelectedIds(prev =>
			prev.includes(seqId) ? prev.filter(id => id !== seqId) : [...prev, seqId]
		);
	};

	const toggleAll = () => {
		const allIds = filteredLines.map(r => r.sequenceId);
		const allSelected = allIds.every(id => selectedIds.includes(id));
		if (allSelected) {
			setSelectedIds(prev => prev.filter(id => !allIds.includes(id)));
		} else {
			setSelectedIds(prev => [...new Set([...prev, ...allIds])]);
		}
		return '';
	};

	// ---------------------------------------------------------------------------
	// Pagination
	// ---------------------------------------------------------------------------
	const searchFields = ['runCode', 'runDate', 'timeOfDay', 'rate', 'length', 'programDescription'];

	const filteredLines = useMemo(() => {
		if (!searchText) return lines;
		const lower = searchText.toLowerCase();
		return lines.filter(row =>
			searchFields.some(f => (String(row[f] || '')).toLowerCase().includes(lower))
		);
	}, [lines, searchText]);

	const visibleRows = useMemo(
		() => filteredLines.slice(page * pageSize, page * pageSize + pageSize),
		[filteredLines, page, pageSize]
	);

	const pageIds = visibleRows.map(r => r.sequenceId);
	const allFilteredIds = filteredLines.map(r => r.sequenceId);
	const allPageSelected = allFilteredIds.length > 0 && allFilteredIds.every(id => selectedIds.includes(id));
	const somePageSelected = allFilteredIds.some(id => selectedIds.includes(id)) && !allPageSelected;

	// ---------------------------------------------------------------------------
	// Header columns definition (widths only — rendering is done inline below)
	// ---------------------------------------------------------------------------
	const headCells = [
		{ field: '__move__', headerName: '', width: 70, align: 'center' },
		{ field: 'sequenceId', headerName: 'Line#', width: 70, align: 'center' },
		{ field: 'runCode', headerName: 'Run', width: 70, align: 'center' },
		{ field: 'programDescription', headerName: 'Program', width: 180, align: 'center' },
		{ field: 'runDate', headerName: 'Run Date', width: 90, align: 'center' },
		{ field: 'timeOfDay', headerName: 'Run Time', width: 90, align: 'center' },
		{ field: 'length', headerName: 'Length', width: 80, align: 'center' },
		{ field: 'rate', headerName: 'Rate', width: 90, align: 'center' },
	];

	// ---------------------------------------------------------------------------
	// Cell renderers
	// ---------------------------------------------------------------------------
	const renderCell = (row, field) => {
		const seqId = row.sequenceId;
		const rowErrs = rowErrors[seqId] || {};
		const hasError = !!rowErrs[field];
		const errorClass = hasError ? ' MuiInput-input-error' : '';
		const isFocused = focusedCell?.seqId === seqId && focusedCell?.field === field;
		const rk = rawKey(seqId, field);

		if (field === '__move__') {
			const rowIndex = lines.findIndex(l => l.sequenceId === seqId);
			return (
				<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
					<Tooltip title="Move Up">
						<span>
							<IconButton size="small" disabled={!canEdit || rowIndex <= 0}
								onClick={e => { e.stopPropagation(); handleMoveLine(seqId, 'up'); }}>
								<ArrowUpwardIcon fontSize="inherit" />
							</IconButton>
						</span>
					</Tooltip>
					<Tooltip title="Move Down">
						<span>
							<IconButton size="small" disabled={!canEdit || rowIndex === lines.length - 1}
								onClick={e => { e.stopPropagation(); handleMoveLine(seqId, 'down'); }}>
								<ArrowDownwardIcon fontSize="inherit" />
							</IconButton>
						</span>
					</Tooltip>
				</div>
			);
		}

		if (field === 'sequenceId') {
			const lineNumber = lines.findIndex(l => l.sequenceId === seqId) + 1;
			return <div style={{ textAlign: 'center' }}>{lineNumber}</div>;
		}

		if (field === 'runCode') {
			const val = row.runCode || 'Y';
			if (!canEdit) {
				return (
					<span style={{ fontWeight: 600, color: val === 'N' ? '#e74c3c' : '#27ae60' }}>{val}</span>
				);
			}
			return (
				<Tooltip title={hasError ? rowErrs[field] : ''} arrow>
					<select
						value={val}
						disabled={!canEdit}
						className={`MuiInputBase-input MuiInput-input${errorClass}`}
						style={{ minWidth: 60, textAlign: 'center', color: val === 'N' ? '#e74c3c' : '#27ae60', fontWeight: 600 }}
						onChange={e => handleSelectChange(seqId, 'runCode', e.target.value)}
					>
						<option value="Y">Y</option>
						<option value="N">N</option>
					</select>
				</Tooltip>
			);
		}

		if (field === 'length') {
			const val = row.length || '30';
			if (!canEdit) {
				return <div style={{ textAlign: 'right' }}>{val}</div>;
			}
			return (
				<Tooltip title={hasError ? rowErrs[field] : ''} arrow>
					<select
						value={val}
						disabled={!canEdit}
						className={`MuiInputBase-input MuiInput-input${errorClass}`}
						style={{ minWidth: 65, textAlign: 'right' }}
						onChange={e => handleSelectChange(seqId, 'length', e.target.value)}
					>
						{LENGTHS.map(l => <option key={l} value={l}>{l}</option>)}
					</select>
				</Tooltip>
			);
		}

		if (field === 'rate') {
			if (!canEdit) {
				return <div style={{ textAlign: 'right' }}>{formatNumber(Number(row.rate || 0))}</div>;
			}
			return (
				<Tooltip title={hasError ? rowErrs[field] : ''} arrow>
					<span style={{ display: 'block', width: '100%' }}>
						<RateCellInput
							value={row.rate}
							disabled={!canEdit}
							errorClass={errorClass}
							onCommit={(fixed) => {
								const nextLines = lines.map(l =>
									l.sequenceId === seqId ? { ...l, rate: fixed } : l
								);
								commitGridValidationState(nextLines, invoiceDocument);
								updateLines(nextLines, { totalSpot: invoiceDocument?.totalSpot ?? nextLines.length });
							}}
						/>
					</span>
				</Tooltip>
			);
		}

		if (field === 'runDate') {
			const displayValue = isFocused && rawValues[rk] !== undefined ? rawValues[rk] : (row.runDate || '');
			if (!canEdit) return <div>{row.runDate}</div>;
			return (
				<Tooltip title={hasError ? rowErrs[field] : ''} arrow>
					<input
						type="text"
						className={`MuiInputBase-input MuiInput-input${errorClass}`}
						style={{ width: '100%' }}
						value={displayValue}
						placeholder="YYMMDD"
						maxLength={6}
						onFocus={() => {
							setFocusedCell({ seqId, field });
							setRawValues(prev => ({ ...prev, [rk]: row.runDate || '' }));
						}}
						onChange={e => handleFieldChange(seqId, field, e.target.value.replace(/\D/g, '').slice(0, 6))}
						onBlur={() => handleFieldBlur(seqId, field)}
					/>
				</Tooltip>
			);
		}

		if (field === 'timeOfDay') {
			if (!canEdit) return <div style={{ textAlign: 'right' }}>{row.timeOfDay}</div>;
			return (
				<Tooltip title={hasError ? rowErrs[field] : ''} arrow>
					<span style={{ display: 'block', width: '100%' }}>
						<TimeOfDayCellInput
							value={row.timeOfDay || ''}
							disabled={!canEdit}
							errorClass={errorClass}
							onCommit={(padded) => {
								const nextLines = lines.map(l =>
									l.sequenceId === seqId
										? sanitizeSpotLine({ ...l, timeOfDay: padded }, 0, invoiceDocument)
										: l
								);
								commitGridValidationState(nextLines, invoiceDocument);
								updateLines(nextLines, { totalSpot: invoiceDocument?.totalSpot ?? nextLines.length });
							}}
						/>
					</span>
				</Tooltip>
			);
		}

		if (field === 'programDescription') {
			const displayValue = isFocused && rawValues[rk] !== undefined ? rawValues[rk] : (row.programDescription || '');
			if (!canEdit) return <div>{row.programDescription}</div>;
			return (
				<Tooltip title={hasError ? rowErrs[field] : ''} arrow>
					<input
						type="text"
						className={`MuiInputBase-input MuiInput-input${errorClass}`}
						style={{ width: '100%' }}
						value={displayValue}
						placeholder="Program"
						onFocus={() => {
							setFocusedCell({ seqId, field });
							setRawValues(prev => ({ ...prev, [rk]: row.programDescription || '' }));
						}}
						onChange={e => handleFieldChange(seqId, field, e.target.value)}
						onBlur={() => handleFieldBlur(seqId, field)}
					/>
				</Tooltip>
			);
		}

		return <div>{row[field]}</div>;
	};

	// ---------------------------------------------------------------------------
	// Render
	// ---------------------------------------------------------------------------
	return (
		<div style={{ width: '100%', marginBottom: 16 }}>
			<Paper sx={{ width: '100%', mb: 2 }} variant="outlined">
				{/* Toolbar */}
					<Grid container sx={{ pt: 1, pl: 1, pr: 1, pb: 1 }} alignItems="center">
						<Grid item xs={12}>
							<Grid container justifyContent="flex-end" alignItems="center" spacing={1}>
								<Grid item>
									<TextField
										sx={{
											'&': {
												paddingRight: '10px',
												fontSize: '12px'
											},
											'& .MuiInput-underline:before': {
												borderBottom: '1px solid rgba(0, 0, 0, 0.42) !important'
											},
											'& .MuiInputAdornment-root': {
												marginRight: '4px'
											}
										}}
										value={searchText}
										onKeyUp={(e) => { setSearchText(e.target.value); setPage(0); }}
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<SearchIcon style={{ width: 20, height: 20, color: 'rgba(0,0,0,0.87)' }} />
												</InputAdornment>
											),
											endAdornment: (searchText &&
												<IconButton size="small" onClick={() => { setSearchText(''); setPage(0); }}>
													<ClearIcon style={{ width: 20, height: 20 }} />
												</IconButton>
											)
										}}
										placeholder="Search..." variant="standard"
									/>
								</Grid>
								<Grid item>
									<span style={{ fontSize: 13, color: '#555' }}>
										Spot Total: <strong style={{ color: '#333' }}>{formatNumber(sumRate)}</strong>
									</span>
								</Grid>
								<Grid item>
									<span style={{ fontSize: 13, color: '#555' }}>
										Spots: <strong style={{ color: '#333' }}>{spotCount}</strong>
									</span>
								</Grid>
								<Grid item sx={{ ml: 2 }}>
									<div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
										<Tooltip title="Delete Line">
											<span style={{ display: 'inline-flex' }}>
												<IconButton size="small" onClick={handleDeleteSelected} disabled={!canDelete}>
													<DeleteIcon />
												</IconButton>
											</span>
										</Tooltip>
										<Tooltip title="Copy Line">
											<span style={{ display: 'inline-flex' }}>
												<IconButton size="small" onClick={handleCopyLine} disabled={!canCopy}>
													<ContentCopyIcon fontSize="small" />
												</IconButton>
											</span>
										</Tooltip>
										<Tooltip title="Add Line">
											<span style={{ display: 'inline-flex' }}>
												<IconButton size="small" onClick={handleAddLine} disabled={!canAdd}>
													<AddBoxIcon />
												</IconButton>
											</span>
										</Tooltip>
									</div>
								</Grid>
							</Grid>
						</Grid>
					</Grid>

				<StyledTableContainer sx={{ width: '100%', height: 560, marginTop: 1 }}>
					<Table stickyHeader sx={{ minWidth: 700 }} aria-label="Broadcast Spot Lines">
						<TableHead>
							<TableRow>
								<TableCell padding="checkbox" style={{ borderLeft: '5px solid transparent' }}>
									<Checkbox
										color="primary"
										indeterminate={somePageSelected}
										checked={allPageSelected}
										onChange={toggleAll}
										disabled={!canEdit}
										inputProps={{ 'aria-label': 'select all rows' }}
									/>
								</TableCell>
								{headCells.map((col) => (
									<TableCell
										key={col.field}
										align={col.align}
										style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
									>
										<TableSortLabel hideSortIcon>
											{col.headerName}
										</TableSortLabel>
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{visibleRows.map((row) => {
								const seqId = row.sequenceId;
								const hasRowError = rowErrors[seqId] && Object.keys(rowErrors[seqId]).length > 0;
								const rowSelected = isSelected(seqId);
								return (
									<TableRow
										key={seqId}
										hover
										selected={rowSelected}
										sx={{ cursor: 'pointer' }}
									>
										<TableCell padding="checkbox"
											style={{ borderLeft: hasRowError ? '5px solid #e74c3c' : '5px solid transparent' }}>
											<Checkbox
												color="primary"
												checked={rowSelected}
												onChange={() => toggleRow(seqId)}
												disabled={!canEdit}
												inputProps={{ 'aria-label': `select row ${seqId}` }}
											/>
										</TableCell>
										{headCells.map((col) => (
											<TableCell
												key={col.field}
												align={col.align}
												style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
												padding="none"
											>
												{renderCell(row, col.field)}
											</TableCell>
										))}
									</TableRow>
								);
							})}
							{lines.length === 0 && (
								<TableRow>
									<TableCell
										align="center"
										style={{ height: 200, borderBottom: 'none' }}
										colSpan={headCells.length + 1}
									>
										<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
											No rows
										</div>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</StyledTableContainer>

				<StyledTablePagination
					rowsPerPageOptions={[10, 25, 50]}
					component="div"
					count={filteredLines.length}
					rowsPerPage={pageSize}
					page={page}
					onRowsPerPageChange={e => { setPageSize(Number(e.target.value)); setPage(0); }}
					onPageChange={(_, newPage) => setPage(newPage)}
				/>
			</Paper>
		</div>
	);
}


